import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { NEVER, Observable } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';
import { ThreadCreationModel, ThreadModel } from '../../models';
import { ThreadAction } from '../../store/actions';
import { ThreadSelectors } from '../../store/selectors';
import { CommentingState } from '../../store/state';
import { IdGenerator } from '../id-generator/id-generator.service';
import { OperationTrackerService } from '../operation-tracker/operation-tracker.service';

@Injectable()
export class ThreadService {
  private isLoadingThreads: boolean;
  private _allStream$: Observable<ThreadModel[]>;

  constructor(
    private store: Store<CommentingState>,
    private operationTrackerService: OperationTrackerService,
    private idGenerator: IdGenerator,
  ) {}

  /**
   * @param threadId
   * @returns Thread by id
   */
  getById(threadId: string): Observable<ThreadModel> {
    return this.store.select(ThreadSelectors.createSelectorById(threadId));
  }

  /**
   * @returns All loaded threads
   */
  getAll(): Observable<ThreadModel[]> {
    if (!this._allStream$) {
      this._allStream$ = this.store.select(ThreadSelectors.selectShouldLoadThreads).pipe(
        switchMap((isLoading) => {
          if (!isLoading) {
            return this.store.select(ThreadSelectors.selectAll);
          }

          if (!this.isLoadingThreads) {
            this.store.dispatch(ThreadAction.LoadAll());
            this.isLoadingThreads = true;
          }

          return NEVER;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }

    return this._allStream$;
  }

  /**
   * Creates new thread
   * @param thread thread to create
   * @returns promise that gets resolved/rejected after operation is complete/failed
   */
  async createAsync(thread: ThreadCreationModel): Promise<ThreadModel> {
    const operationId = this.idGenerator.generateId();
    this.store.dispatch(ThreadAction.Create({ thread, operationId }));
    return this.operationTrackerService.throwIfOperationIsNotSuccessAsync(operationId);
  }

  /**
   * Updates thread's body
   * @param threadId thread id to update body in
   * @param newBody new body
   * @param mentionedEmails emails to be mentioned after body's update
   * @returns promise that gets resolved/rejected after operation is complete/failed
   */
  updateBodyAsync(threadId: string, newBody: string, mentionedEmails?: string[]): Promise<void> {
    return this.updateBodyWithExtraParams(threadId, newBody, mentionedEmails);
  }

  /**
   * @deprecated Don't use it. It will be removed once the API starts storing extra params in db
   * @param threadId
   * @param newBody
   * @param mentionedEmails
   * @param extraParams
   * @returns
   */
  async updateBodyWithExtraParams(
    threadId: string,
    newBody: string,
    mentionedEmails?: string[],
    extraParams?: { [key: string]: any },
  ): Promise<void> {
    const operationId = this.idGenerator.generateId();
    this.store.dispatch(
      ThreadAction.UpdateBody({
        threadId: threadId,
        newBody,
        mentionedEmails,
        extraParams,
        operationId,
      }),
    );
    await this.operationTrackerService.throwIfOperationIsNotSuccessAsync(operationId);
  }

  /**
   * Deletets thread by id
   * @param threadId thread id to delete
   * @returns promise that gets resolved/rejected after operation is complete/failed
   */
  deleteAsync(threadId: string): Promise<void> {
    const operationId = this.idGenerator.generateId();
    this.store.dispatch(ThreadAction.DeleteSingle({ threadId, operationId: operationId }));
    return this.operationTrackerService.throwIfOperationIsNotSuccessAsync(operationId);
  }

  /**
   * Deletes batch of threads
   * @param threadIds thread ids to be deleted
   * @returns promise that gets resolved/rejected after operation is complete/failed
   */
  async batchDeleteAsync(threadIds: string[]): Promise<void> {
    const operationId = this.idGenerator.generateId();
    this.store.dispatch(
      ThreadAction.DeleteBatch({
        threadIdsToDelete: threadIds,
        operationId: operationId,
      }),
    );
    await this.operationTrackerService.throwIfOperationIsNotSuccessAsync(operationId);
  }

  /**
   * Marks thread as resolved
   * @param threadId thread id to be marked as resolved
   * @returns promise that gets resolved/rejected after operation is complete/failed
   */
  async markResolvedAsync(threadId: string): Promise<void> {
    const operationId = this.idGenerator.generateId();
    this.store.dispatch(ThreadAction.ResolveSingle({ threadId, operationId }));
    await this.operationTrackerService.throwIfOperationIsNotSuccessAsync(operationId);
  }

  /**
   * Marks batch of threads as resolved
   * @param threadIds thread ids to be marked as resolved
   * @returns promise that gets resolved/rejected after operation is complete/failed
   */
  async batchMarkResolvedAsync(threadIds: string[]): Promise<void> {
    const operationId = this.idGenerator.generateId();
    this.store.dispatch(
      ThreadAction.ResolveBatch({
        threadIdsToResolve: threadIds,
        operationId: operationId,
      }),
    );
    await this.operationTrackerService.throwIfOperationIsNotSuccessAsync(operationId);
  }

  /**
   * Marks thread as active
   * @param threadId thread id to be marked as active
   * @returns promise that gets resolved/rejected after operation is complete/failed
   */
  async markActiveAsync(threadId: string): Promise<void> {
    const operationId = this.idGenerator.generateId();
    this.store.dispatch(
      ThreadAction.ActivateSingle({
        threadId: threadId,
        operationId,
      }),
    );
    await this.operationTrackerService.throwIfOperationIsNotSuccessAsync(operationId);
  }

  /**
   * Marks batch of threads as active
   * @param threadIds thread ids to be marked as active
   * @returns promise that gets resolved/rejected after operation is complete/failed
   */
  async batchMarkActiveAsync(threadIds: string[]): Promise<void> {
    const operationId = this.idGenerator.generateId();
    this.store.dispatch(
      ThreadAction.ActivateBatch({
        threadIdsToActivate: threadIds,
        operationId: operationId,
      }),
    );
    await this.operationTrackerService.throwIfOperationIsNotSuccessAsync(operationId);
  }
}
